import type { Asset, CustomFieldValue } from '@taki/data-model';
import type { TakiSdk } from '@taki/sdk';
import type { Cell, Column, ViewBinding, ViewType } from '@taki/ui';
import { calendrier, fiche, kanban, liste, tableau } from '@taki/ui/views/core';
import {
  CATEGORY_OPTIONS,
  CONDITION_OPTIONS,
  STATUS_OPTIONS,
  categoryInitials,
  categoryLabel,
  categoryOf,
  conditionLabel,
  customNumber,
  customText,
  formatEuros,
  formatPureDate,
  identityLine,
  statusOf,
} from './data.js';

/**
 * LE CATALOGUE DE VUES ET LES BINDINGS de l'ecran Parc, partages entre l'ecran
 * et le module de reglages. Un seul endroit : sinon la liste deroulante des
 * Parametres et le selecteur de la zone 3c finissent par ne plus proposer la
 * meme chose, et le reglage "vue par defaut" pourrait epingler une vue que
 * l'ecran grise (ViewDefaultSetting lit les MEMES bindings pour griser les
 * memes entrees, avec la meme raison).
 *
 * Les vues sont importees EXPLICITEMENT depuis le sous-chemin
 * `@taki/ui/views/core` : une vue ne s'auto-enregistre jamais, sans cet import
 * le build la supprimerait.
 *
 * Quatre vues sont NOURRIES (liste, tableau, kanban, fiche). Le calendrier est
 * propose MAIS GRISE, et c'est le comportement VOULU : un materiel n'a pas de
 * date metier. La raison detaillee vit sur le binding, plus bas.
 */
export const INVENTAIRE_VIEW_TYPES: ViewType[] = [liste, tableau, kanban, calendrier, fiche];

/** Ids passes a `useScreen` et a `ViewDefaultSetting` : ils doivent coincider. */
export const INVENTAIRE_APP_ID = 'inventaire';
export const INVENTAIRE_SCREEN_ID = 'parc';

/* ------------------------------------------------------------------ */
/* L'ecriture, cote app : traduire une saisie en patch SDK             */
/* ------------------------------------------------------------------ */

/*
 * Une vue n'ecrit JAMAIS elle-meme (elle ne connait pas le SDK) : la fiche
 * appelle le core, et le core appelle le `set()` declare ici. C'est donc le
 * SEUL endroit de l'app ou une saisie devient une ecriture.
 *
 * Deux rappels des Security Rules, valables pour tous les `set()` du fichier :
 * - `type` et `orgId` sont IMMUABLES : ils ne figurent JAMAIS dans un patch,
 *   les rules le refuseraient ;
 * - le SDK fusionne `customFields` CLE PAR CLE (chemins pointes), donc un patch
 *   ne porte QUE la cle touchee : `demoKey` et les cles posees par les autres
 *   apps du pack survivent intactes.
 */

/**
 * La valeur d'un editeur, prete pour le sac `customFields`.
 *
 * Une saisie VIDE vaut `null` : le SDK SUPPRIME alors la cle du document
 * (deleteField), plutot que de laisser une chaine vide qui se relirait comme
 * "renseigne, mais vide". Le type `Cell` porte aussi `Date` et `boolean`, dont
 * aucune colonne du parc ne se sert : on les ramene a une primitive plutot que
 * d'ecrire une valeur que le sac ne sait pas porter.
 */
function toCustomValue(value: Cell): CustomFieldValue {
  if (value === null || value === '') return null;
  if (typeof value === 'number' || typeof value === 'boolean') return value;
  if (value instanceof Date) return value.toISOString();
  return value;
}

/** L'ecriture d'UNE cle du sac `customFields` d'un materiel. */
function writeCustomField(
  sdk: TakiSdk,
  key: string,
): (asset: Asset, value: Cell) => Promise<void> {
  return async (asset, value) => {
    await sdk.assets.update(asset.id, { customFields: { [key]: toCustomValue(value) } });
  };
}

/* ------------------------------------------------------------------ */
/* Ce que SONT les donnees : colonnes et bindings                      */
/* ------------------------------------------------------------------ */

/**
 * Les colonnes du TABLEAU. `value` sert au tri, a la recherche, a l'export et
 * aux agregats : une valeur absente vaut `null` (elle finit en bas du tri quel
 * que soit le sens), jamais `undefined`. `render` n'est que de l'affichage : le
 * tarif se lit "290 EUR" mais se trie et se somme sur le nombre.
 *
 * `set` EST CE QUI REND LA CASE EDITABLE EN FICHE (un clic ouvre l'editeur, en
 * sortir ecrit) : sans lui, aucune affordance, pas meme au survol. Les colonnes
 * ont donc besoin du SDK et ne sont pas de simples constantes de module :
 * elles sont fabriquees ici, comme les bindings, par une fonction appelee UNE
 * fois par consommateur.
 *
 * `kind` n'est declare QUE la ou la deduction du socle se tromperait : elle lit
 * la valeur COURANTE, donc un tarif encore vide (`null`) et une date PURE (une
 * chaine) donneraient tous les deux un editeur de texte. Les colonnes de texte,
 * elles, se deduisent juste et ne declarent rien.
 *
 * PIEGE DES CATALOGUES (`category` ici, `condition` sur la fiche) : `value`
 * rend la valeur STOCKEE ('camera'), jamais son libelle, sinon la liste
 * deroulante ne selectionnerait jamais rien ; c'est `render` qui affiche le mot
 * du metier ("Camera"). Le tri porte donc sur la valeur stockee : pour les cinq
 * categories du parc, l'ordre est le meme que celui des libelles.
 *
 * La colonne "Etat" (la disponibilite) n'est pas declaree ici : elle vient du
 * role `status`, que le tableau rend tout seul avec sa pastille de ton. Son
 * libelle est corrige par le sac libre du binding (`statusLabel`).
 */
function createMaterielColumns(sdk: TakiSdk): Column<Asset>[] {
  return [
    {
      key: 'category',
      label: 'Categorie',
      value: (asset) => (categoryOf(asset) === '' ? null : categoryOf(asset)),
      render: (asset) => categoryLabel(asset),
      sortable: true,
      kind: 'select',
      options: () => CATEGORY_OPTIONS,
      set: writeCustomField(sdk, 'category'),
    },
    {
      key: 'reference',
      label: 'Reference',
      value: (asset) => asset.reference ?? null,
      sortable: true,
      // La reference est un champ NATIF du socle : il n'accepte qu'une chaine
      // NON VIDE et le SDK ne sait pas l'effacer (seul le sac `customFields`
      // sait supprimer une cle). Elle se corrige donc, mais ne se vide pas :
      // une case videe est REFUSEE, la valeur revient et l'echec s'affiche
      // dans la rangee 3, plutot que d'ignorer la saisie en silence.
      set: async (asset, value) => {
        const reference = typeof value === 'string' ? value.trim() : '';
        if (reference === '') {
          throw new Error('La reference ne peut pas etre effacee, seulement corrigee.');
        }
        await sdk.assets.update(asset.id, { reference });
      },
    },
    {
      key: 'dailyRate',
      label: 'Tarif / jour',
      value: (asset) => customNumber(asset, 'dailyRate'),
      render: (asset) => formatEuros(customNumber(asset, 'dailyRate')),
      sortable: true,
      agg: 'sum',
      kind: 'number',
      set: writeCustomField(sdk, 'dailyRate'),
    },
    {
      key: 'purchasePrice',
      label: "Valeur d'achat",
      value: (asset) => customNumber(asset, 'purchasePrice'),
      render: (asset) => formatEuros(customNumber(asset, 'purchasePrice')),
      sortable: true,
      agg: 'sum',
      kind: 'number',
      set: writeCustomField(sdk, 'purchasePrice'),
    },
    {
      key: 'purchaseDate',
      // La date d'achat est une DATE PURE (`YYYY-MM-DD`, doctrine des dates du
      // socle) : la valeur brute se trie chronologiquement telle quelle, le
      // rendu la met en francais, et l'editeur `date` rend la MEME chaine. Ni
      // Timestamp, ni objet Date : le jour glisserait d'un fuseau a l'autre.
      label: 'Achete le',
      value: (asset) => (customText(asset, 'purchaseDate') === '' ? null : customText(asset, 'purchaseDate')),
      render: (asset) => formatPureDate(customText(asset, 'purchaseDate')),
      sortable: true,
      kind: 'date',
      set: writeCustomField(sdk, 'purchaseDate'),
    },
  ];
}

/**
 * Les colonnes de la FICHE : les memes que le tableau, ENRICHIES. La fiche est
 * la vue la plus detaillee de l'app (c'est sa promesse metier : une fiche
 * d'identite complete par exemplaire), elle porte donc aussi la marque, le
 * modele, l'emplacement de stockage, l'etat physique et les notes.
 *
 * Toutes editables en place : depuis la disparition du bouton "Modifier", c'est
 * ICI qu'on corrige un materiel. Une case VIDE reste editable, c'est meme le
 * cas le plus utile (renseigner ce qui manque) : les notes, absentes du
 * formulaire de creation, ne se saisissent que la.
 */
function createFicheColumns(sdk: TakiSdk): Column<Asset>[] {
  return [
    ...createMaterielColumns(sdk),
    {
      key: 'brand',
      label: 'Marque',
      value: (asset) => (customText(asset, 'brand') === '' ? null : customText(asset, 'brand')),
      set: writeCustomField(sdk, 'brand'),
    },
    {
      key: 'model',
      label: 'Modele',
      value: (asset) => (customText(asset, 'model') === '' ? null : customText(asset, 'model')),
      set: writeCustomField(sdk, 'model'),
    },
    {
      key: 'storage',
      label: 'Emplacement',
      value: (asset) => (customText(asset, 'storage') === '' ? null : customText(asset, 'storage')),
      set: writeCustomField(sdk, 'storage'),
    },
    {
      key: 'condition',
      label: 'Etat physique',
      // Meme piege que `category` : la valeur STOCKEE ('bon') cote `value`, le
      // mot du metier ("Bon etat") cote `render`.
      value: (asset) => (customText(asset, 'condition') === '' ? null : customText(asset, 'condition')),
      render: (asset) => conditionLabel(asset),
      kind: 'select',
      options: () => CONDITION_OPTIONS,
      set: writeCustomField(sdk, 'condition'),
    },
    {
      key: 'notes',
      label: 'Notes',
      value: (asset) => (customText(asset, 'notes') === '' ? null : customText(asset, 'notes')),
      set: writeCustomField(sdk, 'notes'),
    },
  ];
}

/**
 * UN BINDING PAR VUE : la meme donnee ne se raconte pas pareil en liste et en
 * kanban.
 *
 * Ces bindings ont besoin du SDK (le kanban ECRIT vraiment la disponibilite au
 * depot d'une carte, la fiche ECRIT chaque champ a la sortie de son editeur),
 * donc ils ne peuvent pas etre de simples constantes de module : ils sont
 * fabriques par cette fonction, que l'ecran ET le module de reglages appellent
 * UNE fois chacun dans un `useMemo` cle sur le SDK (stable). Le resultat est
 * donc stable pour toute la vie de l'ecran, exactement comme une constante :
 * la projection ne se recalcule que quand les donnees ou la requete changent.
 *
 * Les portes de l'ecran (Materiel, Maintenance ; Livraisons a un jeu vide)
 * partagent ces bindings : ce sont les memes materiels, racontes pareil ;
 * seule la tranche chargee change (le chargeur lit `query.section`).
 */
export function createInventaireBindings(sdk: TakiSdk): ViewBinding<Asset>[] {
  return [
    {
      /* LISTE : reconnaitre un exemplaire d'un coup d'oeil. */
      type: 'core.liste',
      roles: {
        title: { get: (asset) => asset.name },
        subtitle: { get: identityLine, empty: 'Reference non renseignee' },
        // Pas de photo dans un parc : la pastille porte les deux premieres
        // lettres de la categorie (CA, OP, LU, SO, MA).
        avatar: { get: categoryInitials },
        status: { get: statusOf, options: () => STATUS_OPTIONS },
      },
    },
    {
      /* TABLEAU : comparer les valeurs du parc (tarifs et valeur d'achat se
         somment en pied de colonne). Sans colonne declaree, l'entree serait
         grisee. */
      type: 'core.tableau',
      roles: {
        title: { get: (asset) => asset.name },
        avatar: { get: categoryInitials },
        status: { get: statusOf, options: () => STATUS_OPTIONS },
      },
      columns: createMaterielColumns(sdk),
      // Sac libre, lu par ce SEUL type de vue : ici le titre n'est pas un nom
      // de personne, et le statut s'appelle l'etat du materiel.
      options: { titleLabel: 'Materiel', statusLabel: 'Etat' },
    },
    {
      /*
       * KANBAN : LE geste de demonstration de l'app. `options()` est
       * obligatoire (sinon le kanban fabrique des colonnes fantomes) et `set()`
       * est ce qui rend la carte glissable : deplacer un materiel d'une colonne
       * a l'autre ECRIT vraiment sa disponibilite en base. Derriere la porte
       * Maintenance, sortir une carte de la colonne "En maintenance" la fait
       * aussi SORTIR du jeu de la porte au rechargement : c'est le geste
       * "reparation terminee", et il est honnete.
       */
      type: 'core.kanban',
      roles: {
        title: { get: (asset) => asset.name },
        subtitle: { get: identityLine, empty: 'Reference non renseignee' },
        avatar: { get: categoryInitials },
        groupKey: {
          get: statusOf,
          options: () => STATUS_OPTIONS,
          set: async (asset, value) => {
            // Rappel des rules : `type` et `orgId` sont IMMUABLES, ils ne
            // figurent jamais dans un patch. Seul le statut bouge ici.
            await sdk.assets.update(asset.id, { status: String(value) });
            // AUCUN `reload()` ici : le core recharge lui-meme apres CHAQUE
            // ecriture reussie (et seulement reussie). C'est ce rechargement
            // qui reconcilie les stats de la zone 2c, calculees sur les
            // enregistrements BRUTS que l'optimisme ne touche pas ; le rappeler
            // ferait deux `list()` pour un seul depot de carte. En cas d'echec,
            // le core remet la carte dans sa colonne d'origine et le DIT dans
            // la rangee 3.
          },
        },
      },
    },
    {
      /*
       * CALENDRIER : DECLARE MAIS GRISE VOLONTAIREMENT.
       *
       * Un materiel n'a PAS de date metier. La date d'achat est un attribut de
       * fiche (quand l'exemplaire est entre au parc), pas un evenement de
       * planning : la poser en `dateStart` remplirait un calendrier de faux
       * evenements, un par materiel, qui ne veulent rien dire pour un loueur.
       * L'entree reste donc proposee et grisee avec sa raison au survol
       * ("Role manquant : dateStart"), jamais retiree du selecteur.
       *
       * Les dates qui meritent un calendrier dans ce pack sont les DEPARTS et
       * les RETOURS : elles appartiennent a l'app reservations, pas ici.
       */
      type: 'core.calendrier',
      roles: {
        title: { get: (asset) => asset.name },
        subtitle: { get: identityLine, empty: 'Reference non renseignee' },
        status: { get: statusOf, options: () => STATUS_OPTIONS },
      },
    },
    {
      /* FICHE : la fiche d'identite complete d'un exemplaire, et le seul
         endroit ou on le MODIFIE (clic sur un champ, ecriture a la sortie).
         Elle affiche les roles puis les colonnes, d'ou le binding le plus
         riche de l'app. */
      type: 'core.fiche',
      roles: {
        // Le NOM s'edite dans l'en-tete de la fiche. Sans ce `set()`, une faute
        // de frappe faite a la creation ne serait plus corrigeable nulle part :
        // la fiche a remplace le Modal d'edition. Meme regle que la reference,
        // un materiel sans nom n'est identifiable dans aucune vue.
        title: {
          get: (asset) => asset.name,
          set: async (asset, value) => {
            const name = typeof value === 'string' ? value.trim() : '';
            if (name === '') {
              throw new Error('Le nom ne peut pas etre efface, seulement corrige.');
            }
            await sdk.assets.update(asset.id, { name });
          },
        },
        subtitle: { get: identityLine, empty: 'Reference non renseignee' },
        avatar: { get: categoryInitials },
        status: { get: statusOf, options: () => STATUS_OPTIONS },
      },
      columns: createFicheColumns(sdk),
      // Le plan de la fiche en DEUX COLONNES de cartes nommees, celui du
      // prototype retenu (v21-fiche-5) : Identification et Valeur sur le
      // premier rang, Sur le terrain ensuite, Notes en pleine largeur (un
      // champ de prose ne se lit pas dans une demi-colonne). Un champ oublie
      // ici tomberait dans la carte "Autres champs" du socle, jamais dans le
      // vide.
      options: {
        cards: [
          { title: 'Identification', keys: ['brand', 'model', 'reference', 'category'] },
          { title: 'Valeur', keys: ['dailyRate', 'purchasePrice', 'purchaseDate'] },
          { title: 'Sur le terrain', keys: ['storage', 'condition'] },
          { title: 'Notes', keys: ['notes'], wide: true },
        ],
      },
    },
  ];
}
